import { useCallback, useEffect, useRef, useState } from "react";

const POLL_INTERVAL_MS = 4000;

/** Where a detail page gets its run and its per-item rows. One per module (sync, descriptions). */
export interface IRunDetailSource<TRun, TEvent, TAction> {
  getRun: (runId: number) => Promise<TRun>;
  getEvents: (params: { runId: number; page: number; action?: TAction }) => Promise<{
    items: TEvent[];
    total: number;
  }>;
  eventsPageSize: number;
  /** True while the run still writes rows, so the page keeps polling. */
  isRunning: (run: TRun) => boolean;
}

export interface IUseRunDetail<TRun, TEvent, TAction> {
  run: TRun | null;
  events: TEvent[];
  isLoading: boolean;
  notFound: boolean;
  currentPage: number;
  totalPages: number;
  eventsTotal: number;
  actionFilter?: TAction;
  setCurrentPage: (page: number) => void;
  setActionFilter: (action?: TAction) => void;
}

/**
 * One run plus a filtered, paginated page of its per-item rows — both fetched in parallel — polled
 * every few seconds while the run is still going, because a run keeps writing rows after the request
 * that started it returned. Polling stops once it finished; a 404 on the run flips `notFound`.
 *
 * Shared by the product-page sync and the descriptions: the two only differ in their fetchers, their
 * DTOs and their status enum, so those come in as `source`.
 */
export const useRunDetail = <TRun, TEvent, TAction>(
  runId: number,
  source: IRunDetailSource<TRun, TEvent, TAction>
): IUseRunDetail<TRun, TEvent, TAction> => {
  const [run, setRun] = useState<TRun | null>(null);
  const [events, setEvents] = useState<TEvent[]>([]);
  const [eventsTotal, setEventsTotal] = useState<number>(0);
  const [currentPage, setCurrentPageState] = useState<number>(1);
  const [actionFilter, setActionFilterState] = useState<TAction | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [notFound, setNotFound] = useState<boolean>(false);

  // Kept in a ref so a caller may build `source` inline: as a dependency it would be a new object
  // every render, and `refresh` would then re-fire in a loop.
  const sourceRef = useRef(source);
  sourceRef.current = source;

  // Guards against setState after unmount when a fetch resolves late. Set on mount as well, so a
  // remount of the same instance does not freeze the view.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const [runResult, eventsPage] = await Promise.all([
      sourceRef.current.getRun(runId),
      sourceRef.current.getEvents({ runId, page: currentPage, action: actionFilter })
    ]);
    if (!mountedRef.current) {
      return;
    }
    setRun(runResult);
    setEvents(eventsPage.items);
    setEventsTotal(eventsPage.total);
  }, [runId, currentPage, actionFilter]);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    (async () => {
      try {
        await refresh();
        if (mounted) {
          setNotFound(false);
        }
      } catch {
        if (mounted) {
          setNotFound(true);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const isRunning = run !== null && source.isRunning(run);

  useEffect(() => {
    if (!isRunning) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      // Swallow transient poll errors — the next tick recovers.
      refresh().catch(() => undefined);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [isRunning, refresh]);

  return {
    run,
    events,
    isLoading,
    notFound,
    currentPage,
    totalPages: Math.ceil(eventsTotal / source.eventsPageSize),
    eventsTotal,
    actionFilter,
    setCurrentPage: setCurrentPageState,
    setActionFilter: (action?: TAction) => {
      setActionFilterState(action);
      setCurrentPageState(1);
    }
  };
};
